import {
  getAuth,
  GoogleAuthProvider,
  FacebookAuthProvider,
  EmailAuthProvider,
  signInWithPopup,
  UserCredential,
  signInWithCustomToken,
  linkWithCredential,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  Auth,
} from 'firebase/auth';
import * as _ from 'lodash-es';
import { AuthUser } from 'types/User';
import fetchGuestToken from './UseCases/fetchGuestToken';
import config from './config';
import authorizedFetch from './authorizedFetch';
import app from './firebaseInitializers';

const firebaseApp = app();
const firebaseAuth = firebaseApp === undefined ? ({} as Auth) : getAuth(firebaseApp);

function isGuest(): boolean {
  const user = firebaseAuth.currentUser;

  return !!user && !!user.email && _.isEmpty(user.providerData);
}

export function isSignedIn(): boolean {
  return !!firebaseAuth.currentUser;
}

async function signInGuestFirebase({
  email,
  uid,
}: {
  email: string;
  uid?: string;
}): Promise<UserCredential> {
  const tokenResponse = await fetchGuestToken({ email, uid });
  return signInWithCustomToken(firebaseAuth, tokenResponse.token);
}

async function linkCredential(email: string, password: string): Promise<UserCredential> {
  const user = firebaseAuth.currentUser;

  if (!user) {
    throw new Error();
  }

  // If a firebase user exists attempt to link the credential
  const credential = EmailAuthProvider.credential(email, password);

  return linkWithCredential(user, credential);
}

async function linkGuestUser(email: string, password: string): Promise<UserCredential> {
  // Use the token generator to refresh any stale tokens
  // first before linking credentials to avoid issues
  // with needing to re-authenticate
  await signInGuestFirebase({ email });
  return linkCredential(email, password);
}

const googleAuthProvider = new GoogleAuthProvider();
googleAuthProvider.addScope('profile');
googleAuthProvider.addScope('email');

const facebookAuthProvider = new FacebookAuthProvider().addScope('email');

export async function sendPasswordResetEmail(email: string): Promise<void> {
  await firebaseSendPasswordResetEmail(firebaseAuth, email);
}

export enum AuthProviders {
  Facebook = 'facebook',
  Google = 'google',
  Password = 'password',
  Guest = 'guest',
  Anon = 'anon',
}

async function ensureAutolistUser(email: string): Promise<void> {
  await authorizedFetch(`${config.coreApiUrl}/users/sign_in`, {
    method: 'POST',
    body: JSON.stringify({
      email,
    }),
  });
}

export async function authAndSignInWithProvider(
  authProvider: AuthProviders
): Promise<UserCredential> {
  const provider =
    authProvider === AuthProviders.Facebook ? facebookAuthProvider : googleAuthProvider;

  const credential = await signInWithPopup(firebaseAuth, provider);

  const email = credential.user?.email;

  if (!email) throw new Error('No email in auth credential');

  await ensureAutolistUser(email);

  return credential;
}

export async function createAccountWithEmailAndPassword(
  email: string,
  password: string
): Promise<UserCredential> {
  if (isGuest()) {
    return linkGuestUser(email, password);
  }

  const credential = await createUserWithEmailAndPassword(firebaseAuth, email, password);

  await ensureAutolistUser(email);

  return credential;
}

export async function signInWithEmailAndPassword(
  email: string,
  password: string
): Promise<UserCredential> {
  if (isGuest()) {
    return linkGuestUser(email, password);
  }

  const credential = await signInWithEmailAndPassword(email, password);

  await ensureAutolistUser(email);

  return credential;
}

export async function signInAsGuest(email: string, uid?: string): Promise<UserCredential> {
  const credential = await signInGuestFirebase({ email, uid });

  await ensureAutolistUser(email);

  return credential;
}

interface OnAuthStateChangedCallback {
  (user?: AuthUser): void;
}

export function onAuthStateChanged(callback: OnAuthStateChangedCallback): () => void {
  return firebaseOnAuthStateChanged(firebaseAuth, (user) => {
    // despite the type signature, the user can actuall be null
    if (!user) {
      callback();
      return;
    }

    const userInfo = {
      email: user.email as string,
      firebaseUid: user.uid,
    };
    callback(userInfo);
  });
}

export function getFirebaseUid(): string | undefined {
  return firebaseAuth.currentUser?.uid;
}

enum AuthLevel {
  anon = 'anon',
  guest = 'guest',
  strong = 'strong',
}

export function getEventAuthLevel(): AuthLevel {
  let authLevel;
  const user = firebaseAuth.currentUser;
  if (user && user.providerData && user.providerData.length > 0) {
    authLevel = AuthLevel.strong;
  } else if (isGuest()) {
    authLevel = AuthLevel.guest;
  } else {
    authLevel = AuthLevel.anon;
  }
  return authLevel;
}

export function getEventAuthProviders(): AuthProviders[] {
  const user = firebaseAuth.currentUser;
  if (!user) {
    return [];
  }
  if (!user.providerData) {
    return [];
  }

  const authProviders: AuthProviders[] = [];

  user.providerData.forEach((provider) => {
    if (!provider?.providerId) {
      return;
    }

    if (provider.providerId === FacebookAuthProvider.PROVIDER_ID) {
      authProviders.push(AuthProviders.Facebook);
    } else if (provider.providerId === GoogleAuthProvider.PROVIDER_ID) {
      authProviders.push(AuthProviders.Google);
    } else if (provider.providerId === EmailAuthProvider.PROVIDER_ID) {
      authProviders.push(AuthProviders.Password);
    }
  });
  return authProviders;
}

export function signOut(): Promise<void> {
  return firebaseAuth.signOut();
}
